/**
 * Non-interactive orientation indicator — three colored arrows (X/Y/Z)
 * pinned to the bottom-right corner of the viewport.
 *
 * Positioned in world space each frame by unprojecting from camera-local
 * coordinates.  We call updateMatrixWorld() before reading the camera
 * matrix so the position is always current — no drift.
 */

import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../app/store.ts";
import {
  COLOR_AXIS_X,
  COLOR_AXIS_Y,
  COLOR_AXIS_Z,
  useSafeAreaInsetBottom,
} from "../ui/tokens.ts";
import { orientBillboardToCamera } from "./billboard.ts";
import { AXIS_COLOR_X, AXIS_COLOR_Y, AXIS_COLOR_Z } from "./gizmo/constants.ts";

// ---------------------------------------------------------------------------
// Geometries (module-level singletons, same pattern as gizmoGeometry.ts)
// ---------------------------------------------------------------------------

const SHAFT = new THREE.CylinderGeometry(0.03, 0.03, 0.7, 6);
SHAFT.translate(0, 0.35, 0);

const HEAD = new THREE.ConeGeometry(0.07, 0.2, 8);
HEAD.translate(0, 0.8, 0);

// ---------------------------------------------------------------------------
// Per-axis constants
// ---------------------------------------------------------------------------

/** Euler rotations to orient the +Y cylinder along each world axis. */
const ROTATIONS: Record<string, THREE.Euler> = {
  x: new THREE.Euler(0, 0, -Math.PI / 2),
  y: new THREE.Euler(0, 0, 0),
  z: new THREE.Euler(Math.PI / 2, 0, 0),
};

/** Label positions — just past the tip of each arrow. */
const LABEL_POS: Record<string, [number, number, number]> = {
  x: [1.05, 0, 0],
  y: [0, 1.05, 0],
  z: [0, 0, 1.05],
};

const LABEL_COLORS: Record<string, string> = {
  x: COLOR_AXIS_X,
  y: COLOR_AXIS_Y,
  z: COLOR_AXIS_Z,
};

const AXIS_HEX: Record<string, number> = {
  x: AXIS_COLOR_X,
  y: AXIS_COLOR_Y,
  z: AXIS_COLOR_Z,
};

const AXES = ["x", "y", "z"] as const;

// Scratch objects — reused every frame to avoid allocation.
const _vec = new THREE.Vector3();
const _labelQuat = new THREE.Quaternion();

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Build a canvas texture containing a single bold letter for an axis label.
 * Returns a Texture that can be used on a MeshBasicMaterial — avoids the
 * GLSL-based troika <Text> component which is incompatible with WebGPU.
 */
function makeAxisLabelTexture(letter: string, color: string): THREE.Texture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = color;
  ctx.font = "bold 48px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(letter, size / 2, size / 2);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Pre-built label textures — one per axis, created once at module level. */
const LABEL_TEXTURES: Record<string, THREE.Texture> = {};

/** Single axis label — billboarded to always face the camera. */
function AxisLabel({ axis }: { axis: string }) {
  const ref = useRef<THREE.Mesh>(null);

  const mat = useMemo(() => {
    if (!LABEL_TEXTURES[axis]) {
      LABEL_TEXTURES[axis] = makeAxisLabelTexture(
        axis.toUpperCase(),
        LABEL_COLORS[axis],
      );
    }
    return new THREE.MeshBasicMaterial({
      map: LABEL_TEXTURES[axis],
      depthTest: false,
      transparent: true,
    });
  }, [axis]);

  const geo = useMemo(() => new THREE.PlaneGeometry(0.4, 0.4), []);

  useFrame(({ camera }) => {
    if (ref.current) orientBillboardToCamera(ref.current, camera, _labelQuat);
  });

  return (
    <mesh ref={ref} position={LABEL_POS[axis]} geometry={geo} material={mat} />
  );
}

/** Single axis arrow (shaft + head). */
function AxisArrow({ axis }: { axis: string }) {
  const mat = useMemo(
    () =>
      new THREE.MeshBasicMaterial({
        color: AXIS_HEX[axis],
        depthTest: false,
        transparent: true,
        opacity: 0.9,
      }),
    [axis],
  );

  return (
    <group rotation={ROTATIONS[axis]}>
      <mesh geometry={SHAFT} material={mat} />
      <mesh geometry={HEAD} material={mat} />
    </group>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AxisIndicator() {
  const show = useStore((s) => s.showAxisIndicator);
  const groupRef = useRef<THREE.Group>(null);
  const safeAreaBottom = useSafeAreaInsetBottom();

  // Disable raycasting & tag for export exclusion
  useEffect(() => {
    if (!show) return;
    const g = groupRef.current;
    if (!g) return;
    g.userData.excludeFromExport = true;
    g.traverse((child) => {
      child.raycast = () => {};
    });
  }, [show]);

  useFrame(({ camera, size }) => {
    if (!show) return;
    const g = groupRef.current;
    if (!g) return;

    // Ensure the camera's world matrix reflects any OrbitControls changes
    // that happened earlier this frame — eliminates positional drift.
    camera.updateMatrixWorld();

    const dist = 2;
    let halfH: number;
    if ((camera as THREE.OrthographicCamera).isOrthographicCamera) {
      const ortho = camera as THREE.OrthographicCamera;
      halfH = ortho.top;
    } else {
      const persp = camera as THREE.PerspectiveCamera;
      const vFov = (persp.fov * Math.PI) / 180;
      halfH = Math.tan(vFov / 2) * dist;
    }
    const halfW = halfH * (size.width / size.height);

    // Bottom-right corner with pixel-based margin.
    // Add half the safe-area-inset-bottom so the indicator clears the toolbar
    // on devices with OS navigation controls (e.g. iPhone home indicator).
    const worldPerPx = (2 * halfH) / size.height;
    const marginBottom = (90 + safeAreaBottom * 0.5) * worldPerPx;
    const marginRight = 90 * worldPerPx;
    _vec.set(halfW - marginRight, -halfH + marginBottom, -dist);
    _vec.applyMatrix4(camera.matrixWorld);
    g.position.copy(_vec);

    // Constant screen size
    g.scale.setScalar(halfH * 0.09);

    // Identity rotation — arrows always show world axes
    g.quaternion.identity();
  });

  if (!show) return null;

  return (
    <group ref={groupRef}>
      {AXES.map((axis) => (
        <AxisArrow key={axis} axis={axis} />
      ))}
      {AXES.map((axis) => (
        <AxisLabel key={`label-${axis}`} axis={axis} />
      ))}
    </group>
  );
}
